import {
  Box,
  Divider,
  IconButton as MuiIconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { theme } from 'config/theme';
import TextWrapIcon from 'remixicon-react/TextWrapIcon';
import DownloadCloud2LineIcon from 'remixicon-react/DownloadCloud2LineIcon';
import ServerLineIcon from 'remixicon-react/ServerLineIcon';
import GridLineIcon from 'remixicon-react/GridLineIcon';
import { ReactComponent as ExpandDiagonalLineIcon } from 'assets/icons/ExpandDiagonalLineIcon.svg';
import { ReactComponent as CollapseDiagonalLineIcon } from 'assets/icons/CollapseDiagonalLineIcon.svg';
import { ReactComponent as RowSpanLineIcon } from 'assets/icons/RowSpanLineIcon.svg';
import { useProjectRetroContext } from '../../context';
import { ArticleFieldsType, ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import dayjs from 'dayjs';
import { exportToCSV, extractPlainText } from 'utility/helpers';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useProjectDetailsQuery from '@buildwise/pages/project/hooks/useProjectDetailsQuery';
import { useArticleInputFields } from '@buildwise/libs/posts';
import { ConfigEntryType } from 'config/config';
import { compact, Dictionary, keyBy } from 'lodash';

function RetrosToolbar({ lessons }: { lessons: ArticleType[] }) {
  const {
    state: { isFullScreen, wrapText, spanRows, view },
    dispatch,
  } = useProjectRetroContext();
  const { projectId = '' } = useParams();
  const { project } = useProjectDetailsQuery({
    projectId,
    shouldFetch: false,
  });
  const { articleFieldsToRender } = useArticleInputFields({
    articleFieldsType: ArticleFieldsType.LESSON,
    isEditing: false,
  });
  const enabledArticleFieldsToRender: Dictionary<ConfigEntryType | undefined> = useMemo(
    () => keyBy(articleFieldsToRender, 'entryCode'),
    [articleFieldsToRender],
  );

  const getColumnLabel = useCallback(
    (name: string) =>
      enabledArticleFieldsToRender[name]?.referenceObject?.label ||
      enabledArticleFieldsToRender[name]?.entryDescription,
    [enabledArticleFieldsToRender],
  );

  const handleExport = (articles: ArticleType[]) => {
    const userTimeZone = dayjs.tz.guess();

    const jsonData = articles.map((article) => {
      const formattedArticle: {
        [key: string]: any;
      } = {
        'job title': article?.userNameTitleToDisplay || '',
        username: article?.userNameToDisplay || '',
        'Created At': dayjs(article?.publishedAt).format(
          userTimeZone === 'Asia/Calcutta' ? 'DD/MM/YYYY' : 'MM/DD/YYYY',
        ),
        Phase: article?.projectPhases?.join(',') || '',
        Milestone: article?.projectMileStones?.join(',') || '',
        [getColumnLabel('title') || 'Title']: article?.title || '',
        [getColumnLabel('description') || 'Description']:
          extractPlainText(article?.description) || '',
        ...(enabledArticleFieldsToRender['lessonTypes']?.isActive && {
          [getColumnLabel('lessonTypes') || 'Lesson Type']: article?.lessonTypes?.join(',') || '',
        }),
        'Created By': article?.userNameToDisplay || '',
      };

      return formattedArticle;
    });

    const headers = compact([
      'Phase',
      'Milestone',
      getColumnLabel('title') || 'Title',
      getColumnLabel('description') || 'Description',
      (enabledArticleFieldsToRender['lessonTypes']?.isActive && getColumnLabel('lessonTypes')) ||
        'Lesson Type',
      'Created At',
      'Created By',
    ]);
    exportToCSV(jsonData, headers, `${project?.name} Retrospectives`);
  };

  return (
    <Container
      sx={{ boxShadow: isFullScreen ? 'none' : `0 4px 24px ${theme.palette.custom.boxShadow}` }}
    >
      <Box display="flex" alignItems="center" gap={1} ml={1} p={0.5}>
        {/* <Tooltip placement="top" title={<TooltipMsg>Wrap Text</TooltipMsg>}>
              <IconButton
                onClick={() =>
                  dispatch(({ state }) => ({ payload: { wrapText: !state.wrapText } }))
                }
                sx={(theme) => ({
                  ...(wrapText && {
                    backgroundColor: theme.palette.custom.tuna,
                    color: theme.palette.custom.white,
                    '&:hover': {
                      color: theme.palette.custom.white,
                      backgroundColor: theme.palette.custom.tuna,
                    },
                  }),
                })}
              >
                <TextWrapIcon size={18} />
              </IconButton>
            </Tooltip> */}
        {/* <Tooltip placement="top" title={<TooltipMsg>Span Rows</TooltipMsg>}>
          <IconButton
            onClick={() => dispatch(({ state }) => ({ payload: { spanRows: !state.spanRows } }))}
            sx={(theme) => ({
              ...(spanRows && {
                backgroundColor: theme.palette.custom.tuna,
                fill: theme.palette.custom.white,
                '&:hover': {
                  backgroundColor: theme.palette.custom.tuna,
                },
                '& svg path': {
                  backgroundColor: theme.palette.custom.tuna,
                },
              }),
            })}
          >
            <RowSpanLineIcon width={18} height={18} />
          </IconButton>
        </Tooltip> */}
        {/* <Divider orientation="vertical" sx={{ height: 30 }} /> */}
        <Tooltip placement="top" title={<TooltipMsg>Feed View</TooltipMsg>}>
          <IconButton
            onClick={() => dispatch({ payload: { view: 'CARD', isFullScreen: false } })}
            sx={(theme) => ({
              ...(view === 'CARD' && {
                backgroundColor: theme.palette.custom.tuna,
                color: theme.palette.custom.white,
                '&:hover': {
                  color: theme.palette.custom.white,
                  backgroundColor: theme.palette.custom.tuna,
                },
              }),
            })}
          >
            <ServerLineIcon size={18} />
          </IconButton>
        </Tooltip>
        <Tooltip placement="top" title={<TooltipMsg>Table View</TooltipMsg>}>
          <IconButton
            onClick={() =>
              dispatch(() => ({
                payload: { view: 'TABLE', isFullScreen: true },
              }))
            }
            sx={(theme) => ({
              ...(view === 'TABLE' && {
                backgroundColor: theme.palette.custom.tuna,
                color: theme.palette.custom.white,
                '&:hover': {
                  color: theme.palette.custom.white,
                  backgroundColor: theme.palette.custom.tuna,
                },
              }),
            })}
          >
            <GridLineIcon size={18} />
          </IconButton>
        </Tooltip>
        <Divider orientation="vertical" sx={{ height: 30 }} />
        {lessons?.length > 0 && (
          <Tooltip placement="top" title={<TooltipMsg>Download</TooltipMsg>}>
            <IconButton
              onClick={() => {
                handleExport(lessons);
              }}
            >
              <DownloadCloud2LineIcon size={18} />
            </IconButton>
          </Tooltip>
        )}
        {/* <Tooltip
          placement="top"
          title={<TooltipMsg>{isFullScreen ? 'Collapse' : 'Expand'}</TooltipMsg>}
        >
          <IconButton
            onClick={() =>
              dispatch(({ state }) => ({
                payload: {
                  ...state,
                  isFullScreen: !state.isFullScreen,
                  view: 'TABLE',
                },
              }))
            }
            sx={(theme) => ({
              ...(isFullScreen && {
                backgroundColor: theme.palette.custom.tuna,
                color: theme.palette.custom.white,
                '&:hover': {
                  color: theme.palette.custom.white,
                  backgroundColor: theme.palette.custom.tuna,
                },
              }),
            })}
          >
            {isFullScreen ? (
              <CollapseDiagonalLineIcon width={18} />
            ) : (
              <ExpandDiagonalLineIcon width={18} />
            )}
          </IconButton>
        </Tooltip> */}
      </Box>
    </Container>
  );
}

const Container = styled(Paper)(({}) => ({
  borderRadius: 4,
}));

const IconButton = styled(MuiIconButton)(({ theme }) => ({
  borderRadius: 4,
  color: theme.palette.custom.tuna,
}));

const TooltipMsg = styled(Typography)(({ theme }) => ({
  color: theme.palette.custom.white,
  fontSize: 12,
  fontWeight: 600,
}));

export default RetrosToolbar;
